/**
 * Centralized API utility for making requests with enhanced error handling.
 *
 * Features:
 * - Request timeout handling (default 120s)
 * - X-Request-ID header capture
 * - Consistent error response format
 * - Automatic Firebase Auth token injection
 * - Backward compatible with existing code
 */

import { auth } from '../firebase';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

/**
 * Get current Firebase Auth token with automatic refresh
 * @returns {Promise<string|null>} Firebase ID token or null if not authenticated
 *
 * Token Refresh Behavior:
 * - getIdToken() without params: Returns cached token if valid, refreshes if expired (recommended)
 * - getIdToken(true): Forces refresh on every call (use for debugging only)
 * - Firebase tokens expire after 1 hour
 * - SDK automatically refreshes expired tokens in the background
 * - If refresh fails (network issue, revoked token), error is caught and returns null
 */
async function getAuthToken() {
    try {
        const user = auth.currentUser;
        if (user) {
            // Get token - Firebase SDK handles refresh automatically if expired
            return await user.getIdToken();
        }
        return null;
    } catch (error) {
        console.error('Error getting auth token:', error);
        return null;
    }
}

/**
 * Enhanced API call with timeout and request ID tracking
 *
 * @param {string} endpoint - API endpoint (e.g., '/api/contact')
 * @param {object} options - Fetch options (method, body, headers, etc.)
 * @param {number} timeout - Timeout in milliseconds (default: 120000ms = 2 minutes)
 * @returns {Promise<{data: any, requestId: string|null}>} Response data and request ID
 * @throws {Error} Enhanced error with requestId and status properties
 */
export async function apiCall(endpoint, options = {}, timeout = 120000) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
   
    try {
        // Get Firebase Auth token and inject it into headers
        const token = await getAuthToken();
        const headers = { ...options.headers };

        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
            ...options,
            headers,
            signal: controller.signal
        });

        clearTimeout(timeoutId);

        // Capture X-Request-ID from response headers
        const requestId = response.headers.get('X-Request-ID');

        if (!response.ok) {
            const errData = await response.json().catch(() => ({}));

            // Create enhanced error with all available information
            const error = new Error(
                errData.detail ||
                errData.message ||
                `Request failed with status ${response.status}`
            );
            error.requestId = errData.request_id || requestId;
            error.status = response.status;
            error.isApiError = true;

            // Add specific error types for better handling
            if (response.status === 401) {
                error.isAuthError = true;
                // If token expired or invalid, user may need to re-login
                console.error('Authentication failed - token may be expired or invalid');
            } else if (response.status === 413) {
                error.isFileSizeError = true;
            } else if (response.status === 408) {
                error.isTimeout = true;
            } else if (response.status >= 500) {
                error.isServerError = true;
            }

            throw error;
        }

        const data = await response.json();

        return {
            data,
            requestId
        };

    } catch (error) {
        clearTimeout(timeoutId);

        // Handle fetch timeout (AbortController)
        if (error.name === 'AbortError') {
            const timeoutError = new Error('Request timeout - please try again');
            timeoutError.isTimeout = true;
            timeoutError.isApiError = true;
            throw timeoutError;
        }

        // Re-throw enhanced errors
        if (error.isApiError) {
            throw error;
        }

        // Wrap unexpected errors
        const wrappedError = new Error(error.message || 'Network error occurred');
        wrappedError.originalError = error;
        wrappedError.isNetworkError = true;
        throw wrappedError;
    }
}

/**
 * Backward compatible API call that returns data directly (like original fetch)
 * Use this to gradually migrate existing code without breaking anything
 *
 * @param {string} endpoint - API endpoint
 * @param {object} options - Fetch options
 * @param {number} timeout - Timeout in milliseconds
 * @returns {Promise<any>} Response data only (no request ID)
 */
export async function apiCallLegacy(endpoint, options = {}, timeout = 120000) {
    const { data } = await apiCall(endpoint, options, timeout);
    return data;
}

/**
 * Validate file size before upload
 *
 * @param {File} file - File object to validate
 * @param {number} maxSizeMB - Maximum file size in MB (default: 10MB)
 * @throws {Error} If file exceeds size limit
 */
export function validateFileSize(file, maxSizeMB = 10) {
    if (!file) {
        throw new Error('No file provided');
    }

    const fileSizeMB = file.size / (1024 * 1024);

    if (fileSizeMB > maxSizeMB) {
        const error = new Error(
            `File size must be under ${maxSizeMB}MB. Current file is ${fileSizeMB.toFixed(2)}MB.`
        );
        error.isFileSizeError = true;
        throw error;
    }

    return true;
}

/**
 * Get user-friendly error message
 *
 * @param {Error} error - Error object
 * @returns {string} User-friendly error message
 */
export function getErrorMessage(error) {
    if (!error) return 'An unknown error occurred';

    if (error.isAuthError) {
        return 'Authentication failed. Please sign in again.';
    }

    if (error.isTimeout) {
        return 'Request timeout - please try again';
    }

    if (error.isFileSizeError) {
        return error.message; // Already user-friendly
    }

    if (error.isServerError) {
        return `Server error: ${error.message}`;
    }

    if (error.isNetworkError) {
        return 'Network error - please check your connection';
    }

    return error.message || 'An error occurred';
}

/**
 * Get API base URL (useful for components)
 */
export function getApiBaseUrl() {
    return API_BASE_URL;
}
